<template>
  <v-dialog
    v-model="dialog"
    max-width="520"
    eager
    :attach="'#app'"
    :retain-focus="false"
  >
    <v-card>
      <v-toolbar flat dense>
        <v-toolbar-title>{{ $t('credentials.alfasign.authorizeTitle') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :disabled="loading" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-progress-linear v-if="loading" indeterminate />

      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-text-field
            v-model.trim="otp"
            :label="$t('credentials.alfasign.otpCode')"

            :rules="[rules.require, rules.otp]"
            :disabled="loading || !readyToConfirm"
            hide-details="auto"
            inputmode="numeric"
            maxlength="8"
            @keydown.enter="confirm"
          />

          <v-text-field
            v-model.trim="pin"
            :label="$t('credentials.alfasign.pinCode')"
            :rules="[rules.required, rules.pin]"
            :type="showPin ? 'text' : 'password'"
            :append-icon="showPin ? 'mdi-eye' : 'mdi-eye-off'"
            :disabled="loading || !readyToConfirm"
            hide-details="auto"
            @click:append="showPin = !showPin"
            @keydown.enter="confirm"
          />
        </v-form>

        <v-alert v-if="error" type="error" dense outlined class="mt-3">
          {{ error }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-btn text :disabled="loading" @click="close">
          {{ $t('main.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!readyToConfirm || !valid"
          @click="confirm"
        >
          {{ $t('dialog.confirmation.title') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import signatures from '@/services/api/signatures'

export default {
  name: 'SignAlfasign',
  data() {
    return {
      dialog: false,
      loading: false,
      error: '',
      valid: false,
      otp: '',
      pin: '',
      showPin: false,
      pending: null,
      rules: {
        required: v => {
          const nonEmpty = v !== null && v !== undefined && String(v).trim() !== ''
          return nonEmpty || (typeof (this.$t && this.$t('validation.form.requiredField')) === 'string'
            ? this.$t('validation.form.requiredField')
            : 'Required')
        },
        otp: v => !!/^\d{6}$/.test(v) ||
        this.$t('credentials.alfasign.otpFormatError'),
        pin: v =>
          !!/^\d{4,12}$/.test(v) ||
        this.$t('credentials.alfasign.pinFormatError')
      }
    }
  },
  computed: {
    readyToConfirm() {
      return !!(this.pending && Array.isArray(this.pending.items) && this.pending.items.length)
    }
  },
  methods: {

    async open(options) {
      this.resetDialog()
      this.dialog = true
      this.loading = true
      this.error = ''

      try {
        const alfaRefId = options?.alfaRefId
        const signaturesInput = Array.isArray(options?.signatures) ? options.signatures : []
        if (!alfaRefId || !signaturesInput.length) {
          throw new Error('Missing alfaRefId or signatures.')
        }

        const normalized = signaturesInput.map(s => ({
          signatureId: s.signatureId,
          currentSignatureId: s.currentSignatureId,
          signatureFieldParameters: s.signatureFieldParameters || s.signatureParameters || s.signatureField,
          comment: s.comment,
          subsignerName: s.subsignerName,
          externalSignedDocId: s.externalSignedDocId
        }))
        const description = options?.description || 'Alfa credential'
        const req = {
          useSeal: false,
          returnAlfaSignatureDatas: true,
          credentialOptions: { description, alfaRefId },
          signatures: normalized
        }
        if (options?.userImageDataUrl) {
          req.userImageDataUrl = options.userImageDataUrl
        }
        if (options?.signatureProfileId) req.signatureProfileId = options.signatureProfileId
        if (Array.isArray(options?.documentsOptions) && options.documentsOptions.length) {
          req.documentsOptions = options.documentsOptions
        }

        const resp = await signatures.sign(req)

        const items = (Array.isArray(resp) ? resp : []).map((r, idx) => ({
          ...normalized[idx],
          alfaSessionId: r.alfaSessionId,
          alfaDocHash: r.docHash
        }))

        this.pending = {
          alfaRefId,
          description,
          signatureProfileId: options?.signatureProfileId || null,
          items,
          userImageDataUrl: options?.userImageDataUrl || null
        }
      } catch (e) {
        this.error = this.handleError(e)
      } finally {
        this.loading = false
      }
    },

    async confirm() {
      if (!this.readyToConfirm) return
      const ok = this.$refs.form && this.$refs.form.validate()
      if (!ok) return

      this.loading = true
      this.error = ''

      try {
        const body = {
          credentialOptions: {
            description: this.pending.description || 'Alfa credential',
            alfaRefId: this.pending.alfaRefId,
            PIN: this.pin
          },
          signatures: this.pending.items.map(it => ({
            signatureId: it.signatureId,
            currentSignatureId: it.currentSignatureId,
            signatureFieldParameters: it.signatureFieldParameters,
            comment: it.comment,
            subsignerName: it.subsignerName,
            externalSignedDocId: it.externalSignedDocId,

            alfaDocHash: it.alfaDocHash,
            alfaSessionId: it.alfaSessionId,
            alfaOtpCode: this.otp
          }))
        }

        if (this.pending.signatureProfileId) {
          body.signatureProfileId = this.pending.signatureProfileId
        }
        if (this.pending.userImageDataUrl) { body.userImageDataUrl = this.pending.userImageDataUrl }
        await signatures.sign(body)
        this.$emit('done')
        this.close()
      } catch (e) {
        this.error = this.handleError(e)
      } finally {
        this.loading = false
      }
    },

    close() {
      this.dialog = false
      this.$emit('cancel')
    },

    resetDialog() {
      this.loading = false
      this.error = ''
      this.valid = false
      this.otp = ''
      this.pin = ''
      this.showPin = false
      this.pending = null
      if (this.$refs.form) this.$refs.form.resetValidation()
    },

    handleError(e) {
      return (e && e.response && (e.response.data?.message || e.response.data?.error)) ||
             e?.message ||
             this.t('toastMessage.errorWhileRetrievingData', 'Unexpected error')
    }
  }
}
</script>
